import {Component, OnDestroy, OnInit} from '@angular/core';
import {
    FormBuilder, FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule, Validators
} from "@angular/forms";
import {
    AppAlertService,
    ParametersService, SettingsModuleInfoService,
    SettingsService,
    SubtitlesService
} from "../../core";
import {
    FormSectionComponent
} from "../../common/forms/form-section/form-section.component";
import {
    FormLabelComponent
} from "../../common/forms/form-label/form-label.component";
import {
    FormTextComponent
} from "../../common/forms/form-text/form-text.component";
import {
    FormItemComponent
} from "../../common/forms/form-item/form-item.component";
import {
    FormTextInputComponent
} from "../../common/forms/form-text-input/form-text-input.component";
import {
    FormCheckboxComponent
} from "../../common/forms/form-checkbox/form-checkbox.component";
import {
    FormButtonComponent
} from "../../common/forms/form-button/form-button.component";
import {CountryData, FormStatus} from "../../models";
import {Subscription} from "rxjs";
import {CountryListComponent} from "./country-list/country-list.component";
import {
    TimeZonesListComponent
} from "./time-zones-list/time-zones-list.component";

@Component({
  selector: 'settings-module',
  standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        FormSectionComponent,
        FormLabelComponent,
        FormTextComponent,
        FormItemComponent,
        FormTextInputComponent,
        FormCheckboxComponent,
        FormButtonComponent,
        CountryListComponent,
        TimeZonesListComponent,
    ],
  templateUrl: './settings-module.component.html',
  styleUrl: './settings-module.component.scss'
})
export class SettingsModuleComponent implements OnInit, OnDestroy {
    moduleForm: FormGroup;
    formStatus: FormStatus = {
        validModuleName: null,
        validAdditionalInformation: null,
        changedPostcodePolicy: null,
        changedCountry: null,
        validZipCode: null,
        changedGmtId: null,
    }
    private selectedModuleSub: Subscription | undefined;
    private moduleNameSub: Subscription | undefined;
    private additionalInformationSub: Subscription | undefined;
    private moduleDefaultSub: Subscription | undefined;
    private postcodePolicySub: Subscription | undefined;
    private countrySub: Subscription | undefined;
    private zipCodeSub: Subscription | undefined;
    private gmtIdSub: Subscription | undefined;


    constructor(
        private appAlert: AppAlertService,
        private fb: FormBuilder,
        public parameters: ParametersService,
        public settings: SettingsService,
        private settingsModuleInfo: SettingsModuleInfoService,
        private subtitles: SubtitlesService,
    ) {
    }

    ngOnInit(): void {
        this.moduleForm = this.fb.group({
            moduleName: [null as null | boolean, [Validators.minLength(2), Validators.maxLength(40)]],
            additionalInformation: [null as null | string, [Validators.maxLength(40)]],
            moduleDefault: [null as null | boolean],
            postcodePolicy:[null as null | boolean],
            country: [null as null | CountryData],
            zipCode: [null as null | string, this.settings.zipCodeValidator()],
            gmtId: [null as null | number],
         })

        this.selectedModuleSub = this.settings.selectedModule$.subscribe(module => {
            if (module) {
                let moduleDefault = module.default;
                if (this.settingsModuleInfo.newDefaultModuleId !== null) {
                    moduleDefault = module.id === this.settingsModuleInfo.newDefaultModuleId;
                }
                this.moduleForm.patchValue({
                    moduleName: module.name,
                    additionalInformation: module.additionalInformation,
                    moduleDefault,
                    postcodePolicy: module.postcodePolicyAccepted,
                    country: module.country,
                    zipCode: module.zipCode,
                    gmtId: module.gmtId,
                });
            }
        });
        this.moduleNameSub = this.moduleForm.get('moduleName')?.valueChanges.subscribe(value => {
            this.formStatus.validModuleName = value;
            this.settings.selectedModule.name = value;
        });
        this.additionalInformationSub = this.moduleForm.get('additionalInformation')?.valueChanges.subscribe(value => {
            this.formStatus.validAdditionalInformation = !!value || value === '';
            this.settings.selectedModule.additionalInformation = value;
        });
        this.moduleDefaultSub = this.moduleForm.get('moduleDefault')?.valueChanges.subscribe(value => {
            if (value) {
                this.settingsModuleInfo.newDefaultModuleId = this.settings.selectedModule.id;
            }
            if (!value && this.settingsModuleInfo.newDefaultModuleId === this.settings.selectedModule.id) {
                this.settingsModuleInfo.newDefaultModuleId = null;
            }
        });
        this.postcodePolicySub = this.moduleForm.get('postcodePolicy')?.valueChanges.subscribe(value => {
            this.formStatus.changedPostcodePolicy = value;
            this.settings.selectedModule.postcodePolicyAccepted = value;
        });
        this.countrySub = this.moduleForm.get('country')?.valueChanges.subscribe(value => {
            if (value) {
                this.formStatus.changedCountry = value.name;
            }
        });
        this.zipCodeSub = this.moduleForm.get('zipCode')?.valueChanges.subscribe(value => {
            this.formStatus.validZipCode = value;
            this.settings.selectedModule.zipCode = value;
        });
        this.gmtIdSub = this.moduleForm.get('gmtId')?.valueChanges.subscribe(value => {
            this.formStatus.changedGmtId = parseInt(value);
        });
    }

    ngOnDestroy(): void {
        this.selectedModuleSub?.unsubscribe();
        this.moduleDefaultSub?.unsubscribe();
        this.moduleNameSub?.unsubscribe();
        this.additionalInformationSub?.unsubscribe();
        this.postcodePolicySub?.unsubscribe();
        this.countrySub?.unsubscribe();
        this.zipCodeSub?.unsubscribe();
        this.gmtIdSub?.unsubscribe();
        this.settingsModuleInfo.newDefaultModuleId = null;
    }

    get moduleNameControl(): FormControl<string | null> {
        return this.moduleForm.get('moduleName') as FormControl<string | null>;
    }

    get additionalInformationControl(): FormControl<string | null> {
        return this.moduleForm.get('additionalInformation') as FormControl<string | null>;
    }

    get moduleDefaultControl(): FormControl<boolean | null> {
        return this.moduleForm.get('moduleDefault') as FormControl<boolean | null>;
    }

    get postcodePolicyControl(): FormControl<boolean | null> {
        return this.moduleForm.get('postcodePolicy') as FormControl<boolean | null>;
    }

    isPostCodePolicyAccepted(): boolean {
        return this.postcodePolicyControl.value === true;
    }

    get countryControl(): FormControl<CountryData | null> {
        return this.moduleForm.get('country') as FormControl<CountryData | null>;
    }

    get zipCodeControl(): FormControl<string | null> {
        return this.moduleForm.get('zipCode') as FormControl<string | null>;
    }

    get timeZoneControl(): FormControl<number | null> {
        return this.moduleForm.get('gmtId') as FormControl<number | null>;
    }

    subtitle(val: number): string {
        return val !== 0 ? this.subtitles.setSubtitle(val) : '';
    }

    updateModuleData(): void {
        if (this.parameters.demoAccount()) {
            this.appAlert.openDemoAccountDialog();
            return;
        } else {
            this.settingsModuleInfo.changeModuleData(this.settings.selectedModule, this.formStatus);
        }
    }
}
